import { type ReactNode, useMemo, useState } from "react";
import { Check, ChevronDown, Minus, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { WFPButton } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

export interface ComboboxOption {
  value: number | string;
  label: string;
  // Optional nesting depth (0 = root). When set, the option is indented in the list
  // by this many levels so a hierarchy reads visually. Ignored for flat option sets.
  depth?: number;
}

interface MultiSelectComboboxProps<T extends ComboboxOption> {
  id: string;
  options: T[];
  value: T[];
  placeholder: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  errorMessage?: string;
  isDisabled?: boolean;
  // Options are still streaming in: show a loading affordance in the trigger and
  // the option list instead of the empty-state message.
  isLoading?: boolean;
  // When true at most one option can be selected: picking a new one replaces the
  // current selection and closes the popover.
  singleSelect?: boolean;
  // Leading icon shown inside the trigger, before the label.
  icon?: ReactNode;
  // Renders the trigger as "{triggerPrefix}: {placeholder}" while nothing is
  // selected (e.g. "Aggregators: All"); once a value is picked only the value shows.
  triggerPrefix?: string;
  // When provided and a value is selected, a trailing clear (X) button replaces the
  // chevron; clicking it resets the selection without opening the popover.
  onClear?: () => void;
  clearLabel?: string;
  // Adds a "Select all" row toggling every option that matches the search (all
  // options when the search is empty). Ignored in single-select mode.
  showSelectAll?: boolean;
  // When more than this many options are selected the trigger shows a count
  // ("12 selected") instead of every selected label.
  summarizeSelectionAfter?: number;
  onChange: (value: T[]) => void;
}

type CheckState = boolean | "indeterminate";

const CheckboxIndicator = ({ checked }: { checked: CheckState }) => (
  <span
    className={cn(
      "flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input",
      checked && "border-primary bg-primary text-primary-foreground"
    )}
  >
    {checked === "indeterminate" ? (
      <Minus className="size-3.5" aria-hidden="true" />
    ) : (
      checked && <Check className="size-3.5" aria-hidden="true" />
    )}
  </span>
);

export function MultiSelectCombobox<T extends ComboboxOption>({
  id,
  options,
  value,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  errorMessage,
  isDisabled = false,
  isLoading = false,
  singleSelect = false,
  icon,
  triggerPrefix,
  onClear,
  clearLabel,
  showSelectAll = false,
  summarizeSelectionAfter,
  onChange,
}: MultiSelectComboboxProps<T>) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const selectedValues = useMemo(() => value ?? [], [value]);
  // "Select all" can select hundreds of options: look selections up in a set.
  const selectedValueSet = useMemo(
    () => new Set(selectedValues.map(selected => selected.value)),
    [selectedValues]
  );

  const isSelected = (option: T) => selectedValueSet.has(option.value);

  const toggleOption = (option: T) => {
    if (isSelected(option)) {
      onChange(selectedValues.filter(selected => selected.value !== option.value));
    } else if (singleSelect) {
      onChange([option]);
      setOpen(false);
    } else {
      onChange([...selectedValues, option]);
    }
  };

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return options;
    }
    return options.filter(option => option.label.toLowerCase().includes(normalizedQuery));
  }, [options, query]);

  const canSelectAll = showSelectAll && !singleSelect && filteredOptions.length > 0;
  const filteredSelectedCount = filteredOptions.filter(isSelected).length;
  const selectAllState: CheckState =
    filteredSelectedCount === 0
      ? false
      : filteredSelectedCount === filteredOptions.length
        ? true
        : "indeterminate";

  // Toggles the options matching the search only: selections outside the search
  // are kept, so successive searches + "Select all" add up.
  const toggleAllFiltered = () => {
    if (selectAllState === true) {
      const filteredValues = new Set(filteredOptions.map(option => option.value));
      onChange(selectedValues.filter(selected => !filteredValues.has(selected.value)));
    } else {
      onChange([...selectedValues, ...filteredOptions.filter(option => !isSelected(option))]);
    }
  };

  const loadingLabel = t("multiselect.default.loading");
  const hasSelection = selectedValues.length > 0;
  const selectionLabel =
    summarizeSelectionAfter !== undefined && selectedValues.length > summarizeSelectionAfter
      ? t("multiselect.default.selectedCount", { count: selectedValues.length })
      : selectedValues.map(option => option.label).join(", ");
  const valueLabel = hasSelection ? selectionLabel : isLoading ? loadingLabel : placeholder;
  // While nothing is selected the prefix frames the placeholder ("Aggregators: All");
  // once a value is chosen only the value is shown, per the filter design.
  const triggerLabel =
    triggerPrefix && !hasSelection ? `${triggerPrefix}: ${valueLabel}` : valueLabel;
  const showClear = Boolean(onClear) && hasSelection;

  return (
    <Popover
      open={open}
      onOpenChange={nextOpen => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setQuery("");
        }
      }}
    >
      <PopoverTrigger asChild>
        <WFPButton
          id={id}
          type="button"
          variant="outline"
          disabled={isDisabled}
          aria-invalid={!!errorMessage}
          aria-describedby={errorMessage ? `${id}-error` : undefined}
          className={cn(
            "h-10 w-full justify-between gap-2 font-normal",
            errorMessage && "border-destructive ring-destructive/20"
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            {icon && (
              <span className="flex size-4 shrink-0 items-center justify-center text-muted-foreground">
                {icon}
              </span>
            )}
            <span className={cn("truncate", !hasSelection && "text-muted-foreground")}>
              {triggerLabel}
            </span>
          </span>
          {showClear ? (
            <span
              role="button"
              tabIndex={0}
              aria-label={clearLabel ?? t("multiselect.default.clear")}
              className="flex size-4 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onPointerDown={event => event.stopPropagation()}
              onClick={event => {
                event.stopPropagation();
                event.preventDefault();
                onClear?.();
              }}
              onKeyDown={event => {
                if (event.key === "Enter" || event.key === " ") {
                  event.stopPropagation();
                  event.preventDefault();
                  onClear?.();
                }
              }}
            >
              <X className="size-4" aria-hidden="true" />
            </span>
          ) : isLoading ? (
            <Spinner className="size-4 shrink-0 opacity-70" />
          ) : (
            <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden="true" />
          )}
        </WFPButton>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) min-w-56 p-0">
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <Input
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder={searchPlaceholder ?? t("multiselect.default.search")}
            className="h-8 border-0 px-0 shadow-none focus-visible:ring-0"
          />
        </div>
        {canSelectAll && (
          <div className="border-b py-1">
            <button
              type="button"
              role="checkbox"
              aria-checked={selectAllState === "indeterminate" ? "mixed" : selectAllState}
              onClick={toggleAllFiltered}
              className="flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-sm font-medium hover:bg-accent hover:text-accent-foreground"
            >
              <CheckboxIndicator checked={selectAllState} />
              <span className="truncate">
                {query.trim()
                  ? t("multiselect.default.selectAllFiltered")
                  : t("multiselect.default.selectAll")}
              </span>
              <span className="ml-auto shrink-0 text-muted-foreground">
                {filteredOptions.length}
              </span>
            </button>
          </div>
        )}
        <ul className="max-h-64 overflow-auto py-1" role="listbox" aria-multiselectable="true">
          {filteredOptions.length === 0 ? (
            <li className="flex items-center gap-2 px-3 py-2 text-muted-foreground text-sm">
              {isLoading ? (
                <>
                  <Spinner className="size-4 shrink-0" />
                  {loadingLabel}
                </>
              ) : (
                (emptyMessage ?? t("multiselect.default.noOptions"))
              )}
            </li>
          ) : (
            filteredOptions.map(option => {
              const selected = isSelected(option);
              const indentStyle =
                option.depth && option.depth > 0
                  ? { paddingInlineStart: `calc(0.75rem + ${option.depth}rem)` }
                  : undefined;
              return (
                <li key={option.value} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    onClick={() => toggleOption(option)}
                    style={indentStyle}
                    className="flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                  >
                    <CheckboxIndicator checked={selected} />
                    <span className="truncate">{option.label}</span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
